import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const ASSET_BASE = '/3d';

function glbUrl(name: string): string {
  return `${ASSET_BASE}/glb/${encodeURIComponent(name)}.glb`;
}

function textureUrl(name: string, ext: string): string {
  return `${ASSET_BASE}/${encodeURIComponent(name)}.${ext}`;
}

export function disposeObject3D(root: THREE.Object3D | null | undefined): void {
  if (!root) return;
  root.traverse((child: any) => {
    if (child.isMesh) {
      child.geometry?.dispose();
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat: any) => {
        if (mat) {
          Object.values(mat).forEach((val: any) => {
            if (val?.isTexture) val.dispose();
          });
          mat.dispose();
        }
      });
    }
  });
}

async function fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  return res.arrayBuffer();
}

export async function loadGLBScene(name: string): Promise<THREE.Group> {
  const buffer = await fetchArrayBuffer(glbUrl(name));
  const view = new Uint8Array(buffer);
  if (view.length < 4 || String.fromCharCode(view[0], view[1], view[2], view[3]) !== 'glTF') {
    throw new Error(`Invalid GLB header for ${name}`);
  }
  return new Promise<THREE.Group>((resolve, reject) => {
    const loader = new GLTFLoader();
    loader.parse(
      buffer,
      '',
      (gltf: any) => {
        const scene = gltf.scene as THREE.Group;
        if (gltf.animations?.length) scene.animations = gltf.animations;
        resolve(scene);
      },
      (err: any) => reject(new Error(`GLB parse failed (${name}): ${String(err)}`)),
    );
  });
}

export async function loadAnimationClip(name: string): Promise<THREE.AnimationClip | null> {
  const buffer = await fetchArrayBuffer(glbUrl(name));
  const view = new Uint8Array(buffer);
  if (view.length < 4 || String.fromCharCode(view[0], view[1], view[2], view[3]) !== 'glTF') {
    return null;
  }
  return new Promise<THREE.AnimationClip | null>((resolve) => {
    const loader = new GLTFLoader();
    loader.parse(
      buffer,
      '',
      (gltf: any) => {
        const clip = gltf.animations?.[0] ?? null;
        // Scene is only used for its animation clip; dispose the rest
        disposeObject3D(gltf.scene);
        resolve(clip);
      },
      () => resolve(null),
    );
  });
}

export async function loadTexture(name: string, ext: string): Promise<THREE.Texture | null> {
  return new Promise((resolve) => {
    const loader = new THREE.TextureLoader();
    loader.load(
      textureUrl(name, ext),
      (texture) => {
        texture.flipY = false;
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;
        resolve(texture);
      },
      undefined,
      () => resolve(null),
    );
  });
}
